import { isTRPCClientError } from '@trpc/client';

import {
  findErrorInCauseChain,
  TrpcAuthNotReadyError,
  TrpcHttpStatusError,
  TrpcTokenRefreshError,
} from './errors';

/**
 * How a tRPC failure is classified in the browser (COI-159). The retry policy
 * in `./retryPolicy` and the `/logout` link in `./authRedirectLink` both act on
 * these; nothing else in either app should re-derive them from an error's
 * shape.
 */

const fetchAbortErrorMessages = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource.',
];

const isAbortLikeObject = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError';

const hasFetchAbortError = (error: unknown, isCause = false): boolean => {
  if (isCause && isAbortLikeObject(error)) {
    return true;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (hasFetchAbortError(error.cause, true)) {
    return true;
  }

  if (
    error.name === 'AbortError' ||
    fetchAbortErrorMessages.some((message) => error.message.includes(message))
  ) {
    return true;
  }

  return false;
};

/**
 * A tRPC failure that is a fetch-level abort / network teardown rather than a
 * response the server actually sent. Says nothing about *why* the fetch died —
 * the reporting decision (web's `shouldReportTrpcQueryError`, the portal's
 * `isUnmountedTrpcQueryFetchError`) decides whether the cause was ours.
 *
 * The envelope check is what makes "rather than a response the server actually
 * sent" true. Matching is by message, and a procedure that fails on its own
 * outbound fetch reports exactly the same text: the server ships the message
 * with the envelope, so an `INTERNAL_SERVER_ERROR` reading `Failed to fetch` is
 * indistinguishable from a dead connection on text alone. An envelope means the
 * request reached the tRPC tier and came back, so it is a settled server
 * failure — retrying it here would be the amplification
 * {@link isTrpcTransientInfraError} refuses for the same reason.
 */
export const isTrpcFetchAbortError = (error: unknown): boolean =>
  isTRPCClientError(error) &&
  error.data === undefined &&
  hasFetchAbortError(error);

/**
 * The server's inbound rate limiter rejected the call (COI-454 / COI-882).
 */
export const isTrpcRateLimitError = (error: unknown): boolean =>
  isTRPCClientError(error) && error.data?.code === 'TOO_MANY_REQUESTS';

/**
 * The HTTP status behind a tRPC failure, from whichever of the three routes
 * carries it (COI-159).
 *
 * 1. `data.httpStatus` — the tRPC envelope's own field, present whenever a tRPC
 *    error formatter produced the body. Checked first: it is the only route
 *    that also tells us the tRPC tier owned the failure.
 * 2. `meta.response.status` — the raw `Response`, which `@trpc/client` records
 *    when the body parsed as JSON but was not a tRPC error envelope. `meta` is
 *    typed `Record<string, unknown>`, so this reads it structurally.
 * 3. The `cause` chain — a {@link TrpcHttpStatusError} from
 *    `createStatusPreservingFetch`. This is the route that carries the
 *    HTML-bodied failures (the `express-jwt` 401, an ALB 5xx); without the
 *    fetch wrapper they arrive with neither of the routes above populated.
 */
export const getTrpcHttpStatus = (error: unknown): number | undefined => {
  if (!isTRPCClientError(error)) {
    return undefined;
  }

  const envelopeStatus = error.data?.httpStatus;
  if (typeof envelopeStatus === 'number') {
    return envelopeStatus;
  }

  const responseStatus =
    // `meta` is `Record<string, unknown>`, so the response it records has to be
    // narrowed by hand; the `typeof` below is what actually validates it.
    (error.meta?.['response'] as { status?: unknown } | undefined)?.status;
  if (typeof responseStatus === 'number') {
    return responseStatus;
  }

  return findErrorInCauseChain(error, TrpcHttpStatusError)?.status;
};

/**
 * 408 is defensive rather than reachable today — nothing between the browser
 * and tRPC answers one — but an intermediary that did would be an infra failure
 * of exactly the same kind, so it is classified as one rather than silently
 * treated as a bug.
 */
const TRANSIENT_INFRA_STATUSES: ReadonlySet<number> = new Set([
  408, 502, 503, 504,
]);

/**
 * The infra tier between the browser and tRPC (ALB, Envoy, Lambda) failed, and
 * nobody has retried it yet (COI-159).
 *
 * **The absence of a tRPC envelope is the load-bearing half of that.** A 5xx
 * that arrives WITH an envelope was produced by the tRPC tier, which means it
 * already came back through `packages/data-layer-client/src/http-client.ts` —
 * `{ maxRetries: 3, retryableStatuses: [502, 503, 504] }` for idempotent
 * methods, i.e. four attempts spent before the browser ever saw it. Retrying
 * that three more times here is 16 attempts against a failing upstream, which
 * is precisely the amplification the one-retry-layer-per-hop principle exists
 * to prevent (ENG-616). So an envelope-carried `BAD_GATEWAY` /
 * `SERVICE_UNAVAILABLE` / `GATEWAY_TIMEOUT` is settled, and terminal here.
 *
 * Those codes still matter — `packages/trpc/src/utils/error-mapping.ts` maps
 * 502/503/504 onto them so an infra blip is legible in telemetry and in the
 * message the user sees rather than folded into `INTERNAL_SERVER_ERROR`. They
 * just do not drive a retry.
 *
 * What is left is the failure the edge generated itself, whose status reaches
 * us only through `getTrpcHttpStatus`'s non-envelope routes — in practice the
 * `cause` chain, since these bodies are HTML (see
 * `createStatusPreservingFetch`).
 */
export const isTrpcTransientInfraError = (error: unknown): boolean => {
  if (!isTRPCClientError(error) || error.data !== undefined) {
    return false;
  }

  const status = getTrpcHttpStatus(error);

  return status !== undefined && TRANSIENT_INFRA_STATUSES.has(status);
};

/**
 * The session is no longer valid — the server answered 401 (COI-159).
 *
 * Terminal, not transient: the token the client holds cannot be made to work by
 * sending it again. `./authRedirectLink` turns this into a single `/logout`
 * navigation.
 */
export const isTrpcUnauthorizedError = (error: unknown): boolean =>
  getTrpcHttpStatus(error) === 401;

/**
 * How long the browser is willing to sit on a rate-limited query before giving
 * up on it — a patience budget, NOT a clamp (COI-159).
 *
 * It must not be applied by shortening the wait. `retryAfterSec` is the
 * limiter's own measure of how long its window still has to run
 * (`Math.ceil(msBeforeNext / 1000)` against a 60s `durationSec` —
 * `packages/trpc/src/middleware/rate-limiter.ts`,
 * `packages/trpc/src/trpc-server.ts`), so retrying before it elapses lands
 * inside the window that just rejected us and spends another point against the
 * same budget. That is the COI-882 failure exactly. Waiting a truncated 10s on
 * a `retryAfterSec` of 45 would reintroduce it for ~83% of the window.
 *
 * So a wait longer than this makes the query terminal instead
 * (`shouldRetryTrpcQuery`): the user gets a failure they can retry themselves,
 * rather than a page that silently hangs for a minute or a retry that makes the
 * limiting worse.
 */
export const MAX_RETRY_AFTER_MS = 10_000;

/**
 * How long the rate limiter asked us to wait, in ms (COI-159). Returned
 * unclamped — the caller must honour it in full or not retry at all; see
 * {@link MAX_RETRY_AFTER_MS}.
 *
 * `retryAfterSec` is a field the limiter middleware adds to the error data
 * (`packages/trpc/src/middleware/rate-limiter.ts`), not part of the typed error
 * shape — so it is read structurally and anything that is not a positive finite
 * number is treated as absent, falling the caller back to plain backoff.
 */
export const getTrpcRetryAfterMs = (error: unknown): number | undefined => {
  if (!isTRPCClientError(error)) {
    return undefined;
  }

  const retryAfterSec =
    // Added to the error data by the rate limiter middleware, so it is outside
    // the typed shape; every check below treats it as untrusted.
    (error.data as { retryAfterSec?: unknown } | undefined)?.retryAfterSec;

  if (typeof retryAfterSec !== 'number' || !Number.isFinite(retryAfterSec)) {
    return undefined;
  }

  if (retryAfterSec <= 0) {
    return undefined;
  }

  return retryAfterSec * 1000;
};

/** Whether the request was aborted because auth had not settled yet. */
export const isTrpcAuthNotReadyError = (error: unknown): boolean =>
  findErrorInCauseChain(error, TrpcAuthNotReadyError) !== undefined;

/** Whether the request was aborted because the silent refresh failed. */
export const isTrpcTokenRefreshError = (error: unknown): boolean =>
  findErrorInCauseChain(error, TrpcTokenRefreshError) !== undefined;
